<template>
    <div class="py-6 lg:py-12 px-8">
        <div class="mx-auto max-w-2xl text-center">
            <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Staff</h2>
            <p class="mt-2 text-md lg:text-lg leading-6 lg:leading-8 text-gray-600 dark:text-gray-400">Meet the people who make getting the latest reliable values, data, updates, leaks and trading information possible!</p>
        </div>
        <div class="mt-8 mx-auto max-w-6xl">
            <ul role="list" class="grid max-w-6xl grid-cols-2 sm:auto-fill-[250px] gap-4">
                <li v-for="staffMember in staff" :key="staffMember.id" class="rounded-xl border border-neutral-200 dark:border-white/5 text-center px-8 py-10 flex flex-col justify-between">
                    <div>
                        <NuxtImg class="mx-auto h-24" :src="staffMember.avatar" />
                        <h3 class="mt-6 text-base font-semibold leading-7 text-gray-800 dark:text-white">{{ staffMember.name }}</h3>
                        <p class="text-sm leading-6 text-primary font-semibold">{{ staffMember.rank }}</p>
                        <p class="mt-4 text-sm leading-6 text-gray-500 font-medium break-all">
                            {{ staffMember.description }}
                        </p>
                    </div>
                    <ul role="list" class="mt-7 flex items-center justify-center gap-x-2.5">
                        <li v-if="staffMember.robloxId">
                            <NuxtLink :to="`https://www.roblox.com/users/${staffMember.robloxId}/profile`" external target="_blank" class="text-gray-400 hover:text-[#cb0b0e]">
                                <svg class="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 267 267" xml:space="preserve">
                                    <g transform="matrix(4.39 0 0 4.39 133.58 133.61)">
                                        <path style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-dashoffset: 0; stroke-linejoin: miter; stroke-miterlimit: 4; opacity: 1;" transform=" translate(-73.13, -26.88)" d="M 57.6 0 L 46.26 42.4 l 42.4 11.35 L 100 11.35 Z m 11.2 19.47 l 11.83 3.17 l -3.17 11.83 l -11.84 -3.17 z" stroke-linecap="round" />
                                    </g>
                                </svg>
                            </NuxtLink>
                        </li>
                        <li v-if="staffMember.linktree">
                            <NuxtLink :to="staffMember.linktree" external target="_blank" class="text-gray-400 hover:text-[#43E660]">
                                <svg class="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 417 512.238">
                                    <path d="M171.274 344.942h74.09v167.296h-74.09V344.942zM0 173.468h126.068l-89.622-85.44 49.591-50.985 85.439 87.829V0h74.086v124.872L331 37.243l49.552 50.785-89.58 85.24H417v70.502H290.252l90.183 87.629L331 381.192 208.519 258.11 86.037 381.192l-49.591-49.591 90.218-87.631H0v-70.502z" />
                                </svg>
                            </NuxtLink>
                        </li>
                        <li v-if="staffMember.twitter">
                            <NuxtLink :to="staffMember.twitter" external target="_blank" class="text-gray-400 hover:text-[#1DA1F2]">
                                <svg class="h-5 w-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M11.4678 8.77491L17.2961 2H15.915L10.8543 7.88256L6.81232 2H2.15039L8.26263 10.8955L2.15039 18H3.53159L8.87581 11.7878L13.1444 18H17.8063L11.4675 8.77491H11.4678ZM9.57608 10.9738L8.95678 10.0881L4.02925 3.03974H6.15068L10.1273 8.72795L10.7466 9.61374L15.9156 17.0075H13.7942L9.57608 10.9742V10.9738Z" />
                                </svg>
                            </NuxtLink>
                        </li>
                        <li v-if="staffMember.instagram">
                            <NuxtLink :to="staffMember.instagram" external target="_blank" class="text-gray-400 hover:text-primary">
                                <svg class="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                                </svg>
                            </NuxtLink>
                        </li>
                        <li v-if="staffMember.youtube">
                            <NuxtLink :to="staffMember.youtube" external target="_blank" class="text-gray-400 hover:text-[#FF0000]">
                                <svg class="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                                    <path d="M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6-11.4 42.9-11.4 132.3-11.4 132.3s0 89.4 11.4 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8 11.4-42.9 11.4-132.3 11.4-132.3s0-89.4-11.4-132.3zm-317.5 213.5V175.2l142.7 81.2-142.7 81.2z"/>
                                </svg>
                            </NuxtLink>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
interface StaffMember {
    id: number;
    name: string;
    avatar: string;
    rank: string;
    description: string;
    robloxId: number;
    linktree: string;
    twitter: string;
    instagram: string;
    youtube: string;
}

const { data: staff } = await useFetch<StaffMember[]>('/api/staff')

useHead({
    title: 'Staff'
})
</script>

<style scoped>
.instagram:hover {
    color: transparent;
    background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
    background: -webkit-radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
    background-clip: text;
    -webkit-background-clip: text;
}
</style>